import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setCopoGeneralDetailsData } from "../../Redux/CoPo/actions";
import {
    TextField,
    Button,
    MenuItem,
    Typography,
    Box,
    Divider,
    IconButton
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import AddPartPopup, { PartOption } from "./SelectPartPopUp";
import PartDetails, { PartDetail } from "./PartDetails";
import BatcheSection, { Batch } from "./BatchSection";
import ToolDetails, { ToolDetail } from "./ToolDetails";
import SelectToolPopup, { ToolOption } from "./SelectToolPopup";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Paper from "@mui/material/Paper";

const deliveryTypeOptions = [
    "Staggered Batchwise",
    "Staggered partwise",
    "Common Dispatch"
];
const poOptions = ["Production", "Sample"];
const gstApplyOptions = ["Yes", "No"];
const currencyOptions = ["USD", "EUR"];
const gstTypeOptions = ["Common", "Part Wise"];
const gstPerOptions: number[] = [5, 12, 18, 28];

const paymentTerms = [
    "100% Advance",
    "50% Advance and balance 50% at the time of delivery",
    "100% at the time of delivery",
    "15 days credit",
    "30 days credit",
    "45 days credit",
    "60 days credit",
    "Others"
];

const incoterms = [
    "Ex Works (EXW)",
    "Free Carrier (FCA)",
    "Free Alongside Ship (FAS)",
    "Free On Board (FOB)",
    "Cost and Freight (CFR)",
    "Cost, Insurance and Freight (CIF)",
    "Carriage Paid (CPT)",
    "Carriage and Insurance Paid (CIP)",
    "Delivery Duty Paid (DDP)",
    "Delivered At Place (DAP)"
];

export interface GeneralDetailsRef {
    isFormValid: () => boolean;
    validateAndShowErrors: () => boolean;
}

export const GeneralDetails = forwardRef<GeneralDetailsRef, any>(
    (props: any, ref) => {
        // const navigate = useNavigate();
        const dispatch = useDispatch();
        /* eslint-disable */
        const autoPopulateData = useSelector(
            (state: any) => state.CoPoReducer.autoPopulateData
        );
        const generalDetailsFormData = useSelector(
            (state: any) => state.CoPoReducer.generalDetailsFormData
        );
        /* eslint-enable */

        /* Initialize state from Redux if available, otherwise default string */
        const [gstApply, setGstApply] = useState(
            generalDetailsFormData?.gstApply || ""
        );
        const [gstTypeValue, setGstTypeValue] = useState<
            "" | "Common" | "Part Wise"
        >(generalDetailsFormData?.gstTypeValue || "");

        const [gstPerValue, setGstPerValue] = useState<number>(
            generalDetailsFormData?.gstPerValue || 0
        );
        const [currency, setCurrency] = useState(
            generalDetailsFormData?.currency || ""
        );
        const [hsn, setHsn] = useState(generalDetailsFormData?.hsn || "");
        const [po, setPo] = useState(generalDetailsFormData?.po || "");
        const [deliveryType, setDeliveryType] = useState(
            generalDetailsFormData?.deliveryType || ""
        );
        /* Ensure date is correctly parsed or null */
        const [deliveryDateCommon, setDeliveryDateCommon] = useState<any>(
            generalDetailsFormData?.deliveryDateCommon
                ? dayjs(generalDetailsFormData.deliveryDateCommon)
                : null
        );
        const [contactPerson, setContactPerson] = useState(
            generalDetailsFormData?.contactPerson || ""
        );
        const [contactNumber, setContactNumber] = useState<number | string>(
            generalDetailsFormData?.contactNumber || ""
        );
        const [contactEmail, setContactEmail] = useState(
            generalDetailsFormData?.contactEmail || ""
        );

        const [popupOpen, setPopupOpen] = useState(false);
        const [parts, setParts] = useState<PartDetail[]>(
            generalDetailsFormData?.parts || []
        );

        const [openToolPopup, setOpenToolPopup] = useState(false);
        const [tools, setTools] = useState<ToolDetail[]>(
            generalDetailsFormData?.tools || []
        );

        const [paymentTerm, setPaymentTerm] = useState(
            generalDetailsFormData?.paymentTerm || ""
        );
        const [incoterm, setIncoterm] = useState(
            generalDetailsFormData?.incoterm || ""
        );
        const [otherTerms, setOtherTerms] = useState(
            generalDetailsFormData?.otherTerms || ""
        );
        const [copoFile, setCopoFile] = useState<File | null>(
            generalDetailsFormData?.copoFile instanceof File
                ? generalDetailsFormData.copoFile
                : null
        );
        const copoInputRef = useRef<HTMLInputElement | null>(null);

        const [paymentTermError, setPaymentTermError] = useState(false);
        const [incotermError, setIncotermError] = useState(false);
        const [poError, setPoError] = useState(false);
        const [deliveryTypeError, setDeliveryTypeError] = useState(false);
        const [deliveryDateError, setDeliveryDateError] = useState(false);
        const [currencyError, setCurrencyError] = useState(false);
        const [gstTypeValueError, setGstTypeValueError] = useState(false);
        const [gstPerValueError, setGstPerValueError] = useState(false);
        const [partsError, setPartsError] = useState("");
        const [copoFileError, setCopoFileError] = useState(false);
        const [showChildErrors, setShowChildErrors] = useState(false);
        const [copoError, setCopoError] = useState("");

        const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

        const selectedToolIds = tools.map((t) => t.id);

        const addParts = (selected: PartOption[]) => {
            const newParts = selected.map((p) => ({
                id: p.id,
                aliasName: p.aliasName,
                partId: p.partId,
                partNumber: p.partNumber,
                hsn,
                uom: p.uom,
                currency,
                quantity: 0,
                unitPrice: 0,
                gstPer: 0,
                totalWithGst: 0,
                totalWithoutGst: 0,
                deliveryDate: "",
                batches: []
            }));

            setParts([...parts, ...newParts]);
            setPopupOpen(false);
        };

        /* Sync state to Redux whenever it changes */
        useEffect(() => {
            if (!autoPopulateData) return;

            if (autoPopulateData?.CORFQRes?.POC_Name && !contactPerson)
                setContactPerson(autoPopulateData.CORFQRes?.POC_Name);
            if (autoPopulateData?.CORFQRes?.POC_Number && !contactNumber)
                setContactNumber(autoPopulateData.CORFQRes?.POC_Number);
            if (autoPopulateData?.CORFQRes?.POC_Email && !contactEmail)
                setContactEmail(autoPopulateData.CORFQRes?.POC_Email);

            const rawLocation =
                autoPopulateData?.CORFQRes?.["Company_Name.Location"];
            const normalizedLocation = rawLocation?.trim()?.toLowerCase();

            if (normalizedLocation) {
                if (normalizedLocation === "international") {
                    if (gstApply !== "No") setGstApply("No");
                } else if (normalizedLocation === "domestic") {
                    if (gstApply !== "Yes") setGstApply("Yes");
                }
            }
        }, [
            autoPopulateData,
            contactPerson,
            contactNumber,
            contactEmail,
            gstApply
        ]);

        const poTotalWithoutGst = useMemo(() => {
            const partsTotal = parts.reduce(
                (sum, part) => sum + Number(part.totalWithoutGst || 0),
                0
            );
            const toolsTotal = tools.reduce(
                (sum, tool) => sum + Number(tool.totalWithoutGst || 0),
                0
            );
            return partsTotal + toolsTotal;
        }, [parts, tools]);

        const poTotalWithGst = useMemo(() => {
            const partsTotal = parts.reduce((sum, part) => {
                const excl = Number(part.totalWithoutGst || 0);
                const gst =
                    gstTypeValue === "Common" ? gstPerValue : part.gstPer || 0;

                return sum + excl + (excl * gst) / 100;
            }, 0);

            const toolsTotal = tools.reduce((sum, tool) => {
                const excl = Number(tool.totalWithoutGst || 0);
                const gst =
                    gstTypeValue === "Common" ? gstPerValue : tool.gstPer || 0;

                return sum + excl + (excl * gst) / 100;
            }, 0);

            return partsTotal + toolsTotal;
        }, [parts, tools, gstTypeValue, gstPerValue]);

        const availableTools = useMemo(() => {
            return (
                autoPopulateData?.PartnerQuoteRes?.filter(
                    (t: any) =>
                        t.Is_Tool_Applicable === "Yes" &&
                        t.Enter_Tool_Name &&
                        t.Enter_Tool_Name.trim() !== ""
                ).map((t: any) => ({
                    id: t.ID,
                    toolName: t.Enter_Tool_Name,
                    uom: t.Tool_UOM,
                    quantity: Number(t.Quantity_Year),
                    unitPrice: "",
                    deliveryDate: ""
                })) || []
            );
        }, [autoPopulateData]);

        const isMounted = useRef(false);

        useEffect(() => {
            if (isMounted.current) {
                props.setIsFormTouched?.(true);
            } else {
                isMounted.current = true;
            }
            const formData = {
                gstApply,
                gstTypeValue,
                gstPerValue,
                currency,
                poTotalWithGst,
                poTotalWithoutGst,
                po,
                deliveryDateCommon: deliveryDateCommon
                    ? dayjs(deliveryDateCommon).format("DD-MMM-YYYY")
                    : "",
                deliveryType,
                contactPerson,
                contactNumber,
                contactEmail,
                hsn,
                // parts,
                // tools,
                parts: parts.map((p) => ({
                    ...p,
                    deliveryDate: p.deliveryDate
                        ? dayjs(p.deliveryDate).format("DD-MMM-YYYY")
                        : "",
                    batches: p.batches?.map((b) => ({
                        ...b,
                        deliveryDate: b.deliveryDate
                            ? dayjs(b.deliveryDate).format("DD-MMM-YYYY")
                            : ""
                    }))
                })),
                tools: tools.map((t) => ({
                    ...t,
                    deliveryDate: t.deliveryDate
                        ? dayjs(t.deliveryDate).format("DD-MMM-YYYY")
                        : ""
                })),
                paymentTerm,
                incoterm,
                otherTerms,
                copoFile
            };
            // console.log("Syncing GeneralDetails to Redux:", formData);
            dispatch(setCopoGeneralDetailsData(formData));
        }, [
            hsn,
            gstApply,
            gstTypeValue,
            gstPerValue,
            currency,
            po,
            poTotalWithGst,
            poTotalWithoutGst,
            deliveryDateCommon,
            deliveryType,
            contactPerson,
            contactNumber,
            contactEmail,
            parts,
            tools,
            paymentTerm,
            incoterm,
            otherTerms,
            copoFile,
            dispatch
        ]);

        useEffect(() => {
            if (gstApply === "Yes") {
                if (currency !== "INR") setCurrency("INR");
            } else if (gstApply === "No") {
                if (gstTypeValue !== "") setGstTypeValue("");
                if (gstPerValue !== 0) setGstPerValue(0);
                if (currency === "INR") setCurrency("");
            }
        }, [gstApply, currency, gstTypeValue, gstPerValue]); // Added checks to prevent overwriting Redux synced data

        useEffect(() => {
            if (gstTypeValue === "Common") {
                setParts((prev) =>
                    prev.map((part) => {
                        const excl = Number(part.totalWithoutGst || 0);
                        const incl = excl + (excl * gstPerValue) / 100;

                        return {
                            ...part,
                            totalWithGst: Number(incl.toFixed(2))
                        };
                    })
                );
                setTools((prev) =>
                    prev.map((tool) => {
                        const excl = Number(tool.totalWithoutGst || 0);
                        const incl = excl + (excl * gstPerValue) / 100;

                        return {
                            ...tool,
                            totalWithGst: Number(incl.toFixed(2))
                        };
                    })
                );
            }
        }, [gstPerValue, gstTypeValue]);

        const location = autoPopulateData?.CORFQRes?.["Company_Name.Location"]
            ?.trim()
            ?.toLowerCase();
        useImperativeHandle(ref, () => ({
            isFormValid: () => {
                const isPaymentValid = !!paymentTerm;
                const isIncotermValid = !!incoterm;
                const isFileValid = !!copoFile;
                const isPoValid = !!po;
                const isDeliveryTypeValid = !!deliveryType;
                const isDeliveryDateValid =
                    deliveryType === "Common Dispatch"
                        ? !!deliveryDateCommon
                        : true;
                const isGstTypeValid =
                    gstApply === "Yes" ? !!gstTypeValue : true;
                const isGstPerValid =
                    gstApply === "Yes" && gstTypeValue === "Common"
                        ? !!gstPerValue
                        : true;
                const isCurrencyValid = gstApply === "No" ? !!currency : true;
                const arePartsValid =
                    parts.length > 0 &&
                    parts.every(
                        (p) =>
                            p.quantity &&
                            p.unitPrice &&
                            (gstTypeValue === "Part Wise"
                                ? !!p.gstPer
                                : true) &&
                            (deliveryType === "Staggered partwise"
                                ? !!p.deliveryDate
                                : true)
                    );
                const areToolsValid =
                    tools.length > 0
                        ? tools.every(
                              (t) => t.deliveryDate && t.quantity && t.unitPrice
                          )
                        : true;
                const representBatchesValid = parts.every((p) => {
                    if (deliveryType === "Staggered Batchwise") {
                        const partBatches = p.batches || [];
                        const batchSum = partBatches.reduce(
                            (sum, b) => sum + Number(b.quantity || 0),
                            0
                        );
                        return (
                            partBatches.length > 0 &&
                            partBatches.every(
                                (b) => b.quantity && b.deliveryDate
                            ) &&
                            Math.abs(batchSum - Number(p.quantity || 0)) < 0.01
                        );
                    }
                    return true;
                });

                return (
                    isPaymentValid &&
                    isIncotermValid &&
                    isFileValid &&
                    isPoValid &&
                    isDeliveryTypeValid &&
                    isDeliveryDateValid &&
                    isGstTypeValid &&
                    isGstPerValid &&
                    isCurrencyValid &&
                    arePartsValid &&
                    areToolsValid &&
                    representBatchesValid
                );
            },

            validateAndShowErrors: () => {
                const isPaymentValid = !!paymentTerm;
                const isIncotermValid = !!incoterm;
                const isFileValid = !!copoFile;
                const isPoValid = !!po;
                const isDeliveryTypeValid = !!deliveryType;
                const isDeliveryDateValid =
                    deliveryType === "Common Dispatch"
                        ? !!deliveryDateCommon
                        : true;
                const isGstTypeValid =
                    gstApply === "Yes" ? !!gstTypeValue : true;
                const isGstPerValid =
                    gstApply === "Yes" && gstTypeValue === "Common"
                        ? !!gstPerValue
                        : true;
                const isCurrencyValid = gstApply === "No" ? !!currency : true;
                const arePartsValid =
                    parts.length > 0 &&
                    parts.every(
                        (p) =>
                            p.quantity &&
                            p.unitPrice &&
                            (gstTypeValue === "Part Wise"
                                ? !!p.gstPer
                                : true) &&
                            (deliveryType === "Staggered partwise"
                                ? !!p.deliveryDate
                                : true)
                    );
                const areToolsValid =
                    tools.length > 0
                        ? tools.every(
                              (t) => t.deliveryDate && t.quantity && t.unitPrice
                          )
                        : true;
                const representBatchesValid = parts.every((p) => {
                    if (deliveryType === "Staggered Batchwise") {
                        const partBatches = p.batches || [];
                        const batchSum = partBatches.reduce(
                            (sum, b) => sum + Number(b.quantity || 0),
                            0
                        );
                        return (
                            partBatches.length > 0 &&
                            partBatches.every(
                                (b) => b.quantity && b.deliveryDate
                            ) &&
                            Math.abs(batchSum - Number(p.quantity || 0)) < 0.01
                        );
                    }
                    return true;
                });

                setPaymentTermError(!isPaymentValid);
                setIncotermError(!isIncotermValid);
                setCopoFileError(!isFileValid);
                setPoError(!isPoValid);
                setDeliveryTypeError(!isDeliveryTypeValid);
                setDeliveryDateError(!isDeliveryDateValid);
                setGstTypeValueError(!isGstTypeValid);
                setGstPerValueError(!isGstPerValid);
                setCurrencyError(!isCurrencyValid);
                setShowChildErrors(true);

                if (parts.length === 0) {
                    setPartsError("At least one part is required");
                } else if (!arePartsValid) {
                    setPartsError("Please fill all details for added parts");
                } else if (!representBatchesValid) {
                    setPartsError(
                        "Total batch quantity must match part quantity and all batch details must be filled"
                    );
                } else if (tools.length > 0 && !areToolsValid) {
                    setPartsError("Please fill all details for added tools");
                } else {
                    setPartsError("");
                }

                const finalValid =
                    isPaymentValid &&
                    isIncotermValid &&
                    isFileValid &&
                    isPoValid &&
                    isDeliveryTypeValid &&
                    isDeliveryDateValid &&
                    isGstTypeValid &&
                    isGstPerValid &&
                    isCurrencyValid &&
                    arePartsValid &&
                    parts.length > 0 &&
                    areToolsValid &&
                    representBatchesValid;

                // console.log("Validation Result:", {
                //   finalValid,
                //   isPaymentValid,
                //   isIncotermValid,
                //   isFileValid,
                //   isPoValid,
                //   isDeliveryTypeValid,
                //   isDeliveryDateValid,
                //   isGstTypeValid,
                //   isGstPerValid,
                //   isCurrencyValid,
                //   arePartsValid,
                //   hasParts: parts.length > 0,
                //   areToolsValid,
                //   representBatchesValid,
                // });

                return finalValid;
            }
        }));

        return (
            <>
                <Typography variant="h6" mb={3}>
                    General Details
                </Typography>

                <Grid container spacing={2}>
                    <Grid size={{ md: 4, xs: 12 }} mb={2}>
                        <TextField
                            fullWidth
                            select
                            label={
                                <>
                                    {" "}
                                    Purchase Order Type{" "}
                                    <span style={{ color: "red" }}>*</span>{" "}
                                </>
                            }
                            value={po}
                            error={poError}
                            helperText={poError ? "PO Type is required" : ""}
                            onChange={(e) => {
                                setPo(e.target.value);
                                setPoError(false);
                            }}
                        >
                            {poOptions.map((po) => (
                                <MenuItem key={po} value={po}>
                                    {po}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid size={{ md: 4, xs: 12 }}>
                        <TextField
                            fullWidth
                            select
                            label={
                                <>
                                    {" "}
                                    Delivery type{" "}
                                    <span style={{ color: "red" }}>*</span>{" "}
                                </>
                            }
                            value={deliveryType}
                            error={deliveryTypeError}
                            helperText={
                                deliveryTypeError
                                    ? "Delivery Type is required"
                                    : ""
                            }
                            onChange={(e) => {
                                setDeliveryType(e.target.value);
                                setDeliveryTypeError(false);
                            }}
                        >
                            {deliveryTypeOptions.map((dType) => (
                                <MenuItem key={dType} value={dType}>
                                    {dType}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ md: 4, xs: 12 }}>
                        {deliveryType === "Common Dispatch" && (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    sx={{ minWidth: "100%" }}
                                    label={
                                        <>
                                            {" "}
                                            Delivery Date{" "}
                                            <span style={{ color: "red" }}>
                                                *
                                            </span>{" "}
                                        </>
                                    }
                                    value={deliveryDateCommon}
                                    minDate={dayjs()}
                                    onChange={(e) => {
                                        setDeliveryDateCommon(e);
                                        setDeliveryDateError(false);
                                    }}
                                    slotProps={{
                                        textField: {
                                            error: deliveryDateError,
                                            helperText: deliveryDateError
                                                ? "Delivery Date is required"
                                                : "",
                                            fullWidth: true
                                        },
                                        field: {
                                            readOnly: true
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        )}
                    </Grid>

                    <Grid size={{ md: 4, xs: 12 }} mb={2}>
                        <TextField
                            fullWidth
                            label="Contact Person"
                            disabled
                            value={contactPerson}
                            onChange={(e) => setContactPerson(e.target.value)}
                            // InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid size={{ md: 4, xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Contact Number"
                            disabled
                            value={contactNumber}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (/^\d*$/.test(value)) {
                                    setContactNumber(Number(value));
                                }
                            }}
                            inputProps={{
                                min: 0,
                                step: 1,
                                pattern: "[0-9]*"
                            }}
                        />
                    </Grid>

                    <Grid size={{ md: 4, xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Contact Email"
                            disabled
                            type="email"
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            error={
                                contactEmail !== "" &&
                                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)
                            }
                            helperText={
                                contactEmail !== "" &&
                                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)
                                    ? "Enter a valid email"
                                    : ""
                            }
                        />
                    </Grid>

                    <Grid size={{ md: 4, xs: 12 }}>
                        <TextField
                            fullWidth
                            select
                            label="GST Applicable"
                            value={gstApply}
                            onChange={(e) => setGstApply(e.target.value)}
                            disabled={
                                !location ||
                                [
                                    "international",
                                    "domestic",
                                    "domenstic"
                                ].includes(location)
                            }
                        >
                            {gstApplyOptions.map((gst) => (
                                <MenuItem key={gst} value={gst}>
                                    {gst}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    {gstApply === "Yes" && (
                        <>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <TextField
                                    fullWidth
                                    select
                                    label={
                                        <>
                                            GST Type{" "}
                                            <span style={{ color: "red" }}>
                                                *
                                            </span>
                                        </>
                                    }
                                    value={gstTypeValue}
                                    error={gstTypeValueError}
                                    helperText={
                                        gstTypeValueError
                                            ? "GST Type is required"
                                            : ""
                                    }
                                    onChange={(e) => {
                                        setGstTypeValue(
                                            e.target.value as
                                                | "Common"
                                                | "Part Wise"
                                        );
                                        setGstTypeValueError(false);
                                    }}
                                >
                                    {gstTypeOptions.map((gstType) => (
                                        <MenuItem key={gstType} value={gstType}>
                                            {gstType}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            {gstTypeValue === "Common" && (
                                <Grid size={{ md: 4, xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        select
                                        label={
                                            <>
                                                Select GST %{" "}
                                                <span style={{ color: "red" }}>
                                                    *
                                                </span>
                                            </>
                                        }
                                        value={gstPerValue || ""}
                                        error={gstPerValueError}
                                        helperText={
                                            gstPerValueError
                                                ? "GST % is required"
                                                : ""
                                        }
                                        onChange={(e) => {
                                            setGstPerValue(
                                                Number(e.target.value)
                                            );
                                            setGstPerValueError(false);
                                        }}
                                    >
                                        {gstPerOptions.map((gst) => (
                                            <MenuItem key={gst} value={gst}>
                                                {gst}%
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            )}
                        </>
                    )}

                    <Grid size={{ md: 4, xs: 12 }}>
                        {gstApply === "No" && (
                            <TextField
                                fullWidth
                                select
                                label={
                                    <>
                                        Currency{" "}
                                        <span style={{ color: "red" }}>*</span>
                                    </>
                                }
                                value={currency}
                                error={currencyError}
                                helperText={
                                    currencyError ? "Currency is required" : ""
                                }
                                onChange={(e) => {
                                    setCurrency(e.target.value);
                                    setCurrencyError(false);
                                }}
                            >
                                {currencyOptions.map((cur) => (
                                    <MenuItem key={cur} value={cur}>
                                        {cur}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    </Grid>
                </Grid>

                <Divider sx={{ mt: 3, mb: 1 }} />

                {/* ================= PART DETAILS ================= */}

                <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    mb={1}
                >
                    <Typography variant="h6" mt={2} mb={2}>
                        Part Details <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => setPopupOpen(true)}
                        disabled={deliveryType === ""}
                    >
                        + ADD PART
                    </Button>
                </Box>
                {partsError && (
                    <Typography
                        color="error"
                        variant="caption"
                        display="block"
                        mb={1}
                    >
                        {partsError}
                    </Typography>
                )}

                <AddPartPopup
                    open={popupOpen}
                    onClose={() => setPopupOpen(false)}
                    parts={
                        autoPopulateData?.CORFQRes?.RFQ_Part_Wise?.map(
                            (item: any) => ({
                                id: item.ID,
                                aliasName: item.Part_Name,
                                partId: item.Part_ID,
                                partNumber: item.Part_Number,
                                uom: item.UOM
                            })
                        ) || []
                    }
                    selectedPartIds={parts.map((p) => p.id)}
                    onSubmit={addParts}
                />

                {parts.map((part) => (
                    <Box
                        sx={{
                            border: "1px solid #e0e0e0",
                            borderRadius: 2,
                            p: 2,
                            mb: 3
                        }}
                    >
                        <div key={part.id}>
                            <PartDetails
                                hsn={hsn}
                                deliveryType={deliveryType}
                                gstApply={gstApply}
                                currency={currency}
                                gstTypeValue={gstTypeValue}
                                gstPerValue={gstPerValue}
                                setGstPerValue={setGstPerValue}
                                gstPerOptions={gstPerOptions}
                                data={part}
                                showErrors={showChildErrors}
                                onChange={(field, value) => {
                                    setParts((prev) =>
                                        prev.map((p) =>
                                            p.id === part.id
                                                ? { ...p, [field]: value }
                                                : p
                                        )
                                    );
                                }}
                                onDelete={() =>
                                    setParts(
                                        parts.filter((p) => p.id !== part.id)
                                    )
                                }
                            />
                            {deliveryType === "Staggered Batchwise" && (
                                <BatcheSection
                                    partId={part.partId}
                                    partQuantity={Number(part.quantity || 0)}
                                    batches={part.batches ?? []}
                                    showErrors={showChildErrors}
                                    onAdd={() => {
                                        setParts((prev) =>
                                            prev.map((p) =>
                                                p.id === part.id
                                                    ? {
                                                          ...p,
                                                          batches: [
                                                              ...(p.batches ??
                                                                  []),
                                                              {
                                                                  id: `${part.id}-${Date.now()}`,
                                                                  quantity: "",
                                                                  deliveryDate:
                                                                      ""
                                                              }
                                                          ]
                                                      }
                                                    : p
                                            )
                                        );
                                    }}
                                    onChange={(batchId, field, value) => {
                                        setParts((prev) =>
                                            prev.map((p) =>
                                                p.id === part.id
                                                    ? {
                                                          ...p,
                                                          batches: (
                                                              p.batches ?? []
                                                          ).map((b) =>
                                                              b.id === batchId
                                                                  ? {
                                                                        ...b,
                                                                        [field]:
                                                                            value
                                                                    }
                                                                  : b
                                                          )
                                                      }
                                                    : p
                                            )
                                        );
                                    }}
                                    onDelete={(batchId) => {
                                        setParts((prev) =>
                                            prev.map((p) =>
                                                p.id === part.id
                                                    ? {
                                                          ...p,
                                                          batches: (
                                                              p.batches ?? []
                                                          ).filter(
                                                              (b) =>
                                                                  b.id !==
                                                                  batchId
                                                          )
                                                      }
                                                    : p
                                            )
                                        );
                                    }}
                                />
                            )}
                        </div>
                    </Box>
                ))}

                <Divider sx={{ mb: 2, mt: 2 }} />

                {/* ================= TOOL DETAILS ================= */}

                <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    mb={1}
                >
                    <Typography variant="h6" mt={2} mb={2}>
                        Tool Details
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => setOpenToolPopup(true)}
                        disabled={availableTools.length === 0}
                    >
                        + ADD Tool
                    </Button>
                </Box>

                <SelectToolPopup
                    open={openToolPopup}
                    tools={availableTools}
                    selectedToolIds={selectedToolIds}
                    onClose={() => setOpenToolPopup(false)}
                    onSubmit={(selected) => {
                        setTools([
                            ...tools,
                            ...selected.map((t) => ({
                                id: t.id,
                                toolName: t.toolName,
                                uom: t.uom,
                                quantity: t.quantity,
                                unitPrice: 0,
                                deliveryDate: "",
                                currency,
                                gstApply
                            }))
                        ]);
                        setOpenToolPopup(false);
                    }}
                />

                {tools.map((tool) => (
                    <ToolDetails
                        key={tool.id}
                        data={tool}
                        commonGstPer={gstPerValue}
                        currency={currency}
                        gstApply={gstApply}
                        gstPer={gstPerValue}
                        gstTypeValue={gstTypeValue}
                        gstPerValue={gstPerValue}
                        setGstPerValue={setGstPerValue}
                        gstPerOptions={gstPerOptions}
                        showErrors={showChildErrors}
                        onChange={(field, value) =>
                            setTools((prev) =>
                                prev.map((t) =>
                                    t.id === tool.id
                                        ? { ...t, [field]: value }
                                        : t
                                )
                            )
                        }
                        onDelete={() =>
                            setTools((prev) =>
                                prev.filter((t) => t.id !== tool.id)
                            )
                        }
                    />
                ))}

                <Divider sx={{ mt: 4, mb: 2 }} />

                {/* ================= PURCHASE ORDER VALUE ================= */}

                <Typography variant="h6" mb={2}>
                    Purchase Order Value
                </Typography>

                <Grid container spacing={2}>
                    <Grid size={{ md: 4, xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Total Purchase Order Value (excluding GST)"
                            value={poTotalWithoutGst.toFixed(2)}
                            disabled
                        />
                    </Grid>

                    {gstApply === "Yes" && (
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Total Purchase Order Value (including GST)"
                                value={poTotalWithGst.toFixed(2)}
                                disabled
                            />
                        </Grid>
                    )}
                </Grid>

                <Divider sx={{ mt: 4, mb: 2 }} />

                {/* ================= TERMS DETAILS ================= */}

                <Typography variant="h6" mb={2}>
                    Terms and Conditions
                </Typography>

                <Grid container spacing={2}>
                    <Grid size={{ md: 4, xs: 12 }}>
                        <TextField
                            select
                            fullWidth
                            label={
                                <>
                                    Payment Term{" "}
                                    <span style={{ color: "red" }}>*</span>
                                </>
                            }
                            value={paymentTerm}
                            error={paymentTermError}
                            helperText={
                                paymentTermError
                                    ? "Payment Term is required"
                                    : ""
                            }
                            onChange={(e) => {
                                setPaymentTerm(e.target.value);
                                setPaymentTermError(false);
                            }}
                        >
                            {paymentTerms.map((term) => (
                                <MenuItem key={term} value={term}>
                                    {term}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid size={{ md: 4, xs: 12 }}>
                        <TextField
                            select
                            fullWidth
                            label={
                                <>
                                    Incoterm{" "}
                                    <span style={{ color: "red" }}>*</span>
                                </>
                            }
                            value={incoterm}
                            error={incotermError}
                            helperText={
                                incotermError ? "Incoterm is required" : ""
                            }
                            onChange={(e) => {
                                setIncoterm(e.target.value);
                                setIncotermError(false);
                            }}
                        >
                            {incoterms.map((term) => (
                                <MenuItem key={term} value={term}>
                                    {term}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid size={{ md: 4, xs: 12 }}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Other Terms and Conditions"
                            value={otherTerms}
                            onChange={(e) => {
                                const input = e.target.value;
                                // const filteredInput = input?.replace(/[^a-zA-Z0-9\s]/g, ""); // allow letters, numbers, and spaces
                                if (input?.length <= 1000) {
                                    setOtherTerms(input);
                                }
                            }}
                            inputProps={{ maxLength: 1000 }}
                            helperText={`${otherTerms?.length}/1000 characters`}
                        />
                    </Grid>
                </Grid>

                {/* ================= Customer Documents ================= */}
                <Divider sx={{ mt: 4, mb: 2 }} />

                <Typography variant="h6" mb={0.5}>
                    Company Documents
                </Typography>

                <Typography variant="body2" mb={2}>
                    Upload Purchase Order received from the Customer
                </Typography>

                <Typography fontWeight={600} mb={1}>
                    Company Purchase Order{" "}
                    <span style={{ color: "red" }}>*</span>
                </Typography>

                {/* ================= EMPTY STATE ================= */}
                <Grid container spacing={2}>
                    <Grid size={{ md: 5, xs: 12 }}>
                        {!copoFile && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    borderStyle: "dashed",
                                    borderRadius: 2,
                                    p: 4,
                                    textAlign: "center",
                                    cursor: "pointer"
                                }}
                                onClick={() => copoInputRef.current?.click()}
                            >
                                <UploadFileIcon
                                    sx={{ fontSize: 40, color: "#1976d2" }}
                                />

                                <Typography mt={1}>
                                    <span
                                        style={{
                                            color: "#1976d2",
                                            fontWeight: 500
                                        }}
                                    >
                                        Upload
                                    </span>{" "}
                                    or drag and drop
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    PDF (max. 10MB)
                                </Typography>

                                <input
                                    ref={copoInputRef}
                                    type="file"
                                    hidden
                                    accept="application/pdf"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;

                                        if (file.type !== "application/pdf") {
                                            setCopoError(
                                                "Only PDF files are allowed"
                                            );
                                            return;
                                        }

                                        if (file.size > MAX_FILE_SIZE) {
                                            setCopoError(
                                                "File size should be less than 10MB"
                                            );
                                            return;
                                        }

                                        setCopoError("");
                                        setCopoFile(file);
                                    }}
                                />
                            </Paper>
                        )}

                        {/* ================= FILE SELECTED STATE ================= */}
                        {copoFile && (
                            <Paper
                                elevation={1}
                                sx={{
                                    p: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    borderRadius: 2
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={2}>
                                    <UploadFileIcon color="primary" />

                                    <Box>
                                        <Typography fontWeight={500}>
                                            {copoFile.name}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {(copoFile.size / 1024).toFixed(0)}
                                            kb • Complete
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box display="flex" alignItems="center" gap={1}>
                                    <IconButton
                                        onClick={() => {
                                            setCopoFile(null);
                                            setCopoError("");
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                    <CheckCircleIcon sx={{ color: "green" }} />
                                </Box>
                            </Paper>
                        )}

                        {/* ================= ERROR ================= */}
                        <Typography color="error" mt={1}>
                            {copoError ||
                                (copoFileError ? "File is required" : "")}
                        </Typography>
                    </Grid>
                </Grid>
            </>
        );
    }
);
